import { useState } from 'react';
import { User, Users } from 'lucide-react';
import type { PublicService } from '@rx-manager/shared';
import { useI18n } from '../../../lib/i18n';
import { Button, FOCUS_RING, StepActions, cn } from '../../../components/ui';

/**
 * Track CGS (CGS-D9, D10) — "How many people?", before the time: one booking for up to the
 * service's size, seen back to back by one pharmacist. One person is today's booking, untouched.
 *
 * Radios, not a listbox: the time grid is the page's one set of options.
 */
export function PeopleStep({
  service,
  value,
  onContinue,
  onBack,
  showBack,
}: {
  service: PublicService & { party: { max: number; extraMin: number } };
  value: number;
  onContinue: (people: number) => void;
  onBack: () => void;
  /** No step before this one to go back to. */
  showBack: boolean;
}) {
  const { t } = useI18n();
  const [people, setPeople] = useState(value);
  const choices = Array.from({ length: service.party.max }, (_, i) => i + 1);
  const minutes = (n: number) => service.durationMin + (n - 1) * service.party.extraMin;

  return (
    <div className="space-y-6">
      <fieldset className="space-y-3">
        <legend className="mb-1 text-base text-muted-foreground">{t('people.help')}</legend>
        {choices.map((n) => {
          const id = `people-${n}`;
          const chosen = people === n;
          return (
            <label
              key={n}
              htmlFor={id}
              className={cn(
                'flex min-h-14 cursor-pointer items-center gap-4 rounded-xl border bg-card p-4 transition-colors',
                chosen ? 'border-primary bg-accent/60' : 'border-border hover:border-primary/60',
                'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
              )}
            >
              <input
                id={id}
                type="radio"
                name="people"
                value={n}
                checked={chosen}
                onChange={() => setPeople(n)}
                className={cn('h-5 w-5 shrink-0 accent-[hsl(var(--primary))]', FOCUS_RING)}
              />
              {n === 1 ? (
                <User className="h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
              ) : (
                <Users className="h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-base font-semibold text-heading">
                  {n === 1 ? t('people.justMe') : t('people.count', { count: n })}
                </span>
                {n > 1 && (
                  <span className="block text-sm text-muted-foreground">
                    {t('people.together', { minutes: minutes(n) })}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </fieldset>
      <p className="text-sm text-muted-foreground">{t('people.eachOwn')}</p>
      <StepActions>
        {showBack && (
          <Button variant="outline" onClick={onBack}>
            {t('step.back')}
          </Button>
        )}
        <Button onClick={() => onContinue(people)}>{t('step.continue')}</Button>
      </StepActions>
    </div>
  );
}
